/**
 * 4e.B.5 — fetch every draft created during a single workflow run and derive
 * the per-step view model the `WorkflowProgressCard` renders.
 *
 * Missing steps (e.g. the workflow froze at step 2 of 5 under
 * `freeze-and-ask`) are filled with `status: 'pending'` so the card always
 * shows the full plan, not just the drafts that exist today.
 */
import { useQuery } from '@tanstack/react-query';
import type { AiDraftItem, AiDraftItemStatus } from '@lognai/plugin-ai-core/src/types/draft-item';
import { listAiDraftsByWorkflowRun } from '../api/drafts';
import { aiQueryKeys } from './query-keys';
import { useWorkflows } from './use-workflows';
import type { WorkflowStepStatus, WorkflowStepView } from '../components/WorkflowProgressCard/types';

export interface UseWorkflowRunResult {
  workflowId: string | undefined;
  workflowDisplayName: string | undefined;
  totalSteps: number;
  steps: WorkflowStepView[];
  isLoading: boolean;
  isError: boolean;
}

const DRAFT_STATUS_TO_STEP_STATUS: Record<AiDraftItemStatus, WorkflowStepStatus> = {
  pending: 'pending',
  approved: 'pending',
  applied: 'applied',
  rejected: 'skipped',
  failed: 'failed',
  skipped: 'skipped',
  conflict: 'failed',
  rolled_back: 'undone',
};

const mapDraftStatus = (item: AiDraftItem): WorkflowStepStatus => {
  // Server-side 'partial' (freeze-and-ask) takes precedence — it tells the
  // user the run is frozen, regardless of the draft's own status.
  if (item.workflowRunStatus === 'partial') return 'partial';
  return DRAFT_STATUS_TO_STEP_STATUS[item.status] ?? 'pending';
};

const deriveToolName = (item: AiDraftItem): string => {
  // The agent stores the originating tool name in payload.__toolName when
  // workflows run; fall back to the draftKind which mirrors the tool name
  // for create/update kinds (e.g. "ai.draftCreateAllocation").
  const candidate = (item.payload as Record<string, unknown>)?.['__toolName'];
  if (typeof candidate === 'string' && candidate.length > 0) return candidate;
  return item.draftKind;
};

export const deriveStepsFromDrafts = (drafts: AiDraftItem[]): {
  workflowId: string | undefined;
  totalSteps: number;
  steps: WorkflowStepView[];
} => {
  if (drafts.length === 0) {
    return { workflowId: undefined, totalSteps: 0, steps: [] };
  }

  const workflowId = drafts[0]?.workflowId ?? undefined;
  const stepCount = drafts[0]?.workflowStepCount ?? drafts.length;

  // Sorted by createdAt asc already (server-side). 0-based index = creation order.
  const byIndex = new Map<number, AiDraftItem>();
  drafts.forEach((d, i) => byIndex.set(i, d));

  const steps: WorkflowStepView[] = [];
  for (let i = 0; i < stepCount; i++) {
    const draft = byIndex.get(i);
    if (!draft) {
      steps.push({
        index: i,
        toolName: 'pending step',
        status: 'pending',
      });
      continue;
    }
    const step: WorkflowStepView = {
      index: i,
      toolName: deriveToolName(draft),
      toolLabel: draft.uiLabel,
      status: mapDraftStatus(draft),
      draftId: draft.id,
    };
    if (step.status === 'failed' && draft.failureMessage) {
      step.errorMessage = draft.failureMessage;
    }
    steps.push(step);
  }

  return { workflowId, totalSteps: stepCount, steps };
};

export const useWorkflowRun = (workflowRunId: string | null): UseWorkflowRunResult => {
  const query = useQuery<AiDraftItem[]>({
    queryKey: workflowRunId
      ? aiQueryKeys.workflowRun(workflowRunId)
      : [...aiQueryKeys.all, 'workflowRun', 'null'],
    queryFn: () => listAiDraftsByWorkflowRun(workflowRunId!),
    enabled: Boolean(workflowRunId),
  });

  // The WorkflowSpec registry is fetched once per session (cached with
  // staleTime: Infinity in `useWorkflows`). This lets us resolve the
  // server-side workflow id to its human-readable display name without
  // burdening every callsite with another query.
  const { resolveDisplayName } = useWorkflows();

  const drafts = query.data ?? [];
  const { workflowId, totalSteps, steps } = deriveStepsFromDrafts(drafts);

  return {
    workflowId,
    // Fall back to the id when (a) the workflows query has not resolved yet
    // or (b) the id is not in the registry (e.g. an old run for a workflow
    // that has since been removed). The id is opaque but always non-empty,
    // so the card still shows something stable.
    workflowDisplayName: resolveDisplayName(workflowId) ?? workflowId,
    totalSteps,
    steps,
    isLoading: query.isLoading,
    isError: query.isError,
  };
};
